<template>
  <div class="prod-page">
    <ProductionPageHeader
      title="Projects"
      subtitle="Umbrella campaigns and series. Productions sit under a project."
    >
      <template #actions>
        <button type="button" class="move-btn-primary" @click="showForm = !showForm">
          <span class="material-icons text-lg">{{ showForm ? 'close' : 'add' }}</span>
          {{ showForm ? 'Close' : 'New project' }}
        </button>
      </template>
    </ProductionPageHeader>

    <form v-if="showForm" class="move-card prod-panel space-y-5" @submit.prevent="createProject">
      <ProductionSectionHead title="New project" subtitle="Name the campaign, then add productions underneath it." />
      <div class="grid gap-4 sm:grid-cols-2">
        <div class="move-field !mb-0">
          <label for="project-name">Name</label>
          <input id="project-name" v-model="form.name" required class="move-input" placeholder="Face of Tiye 2026" />
        </div>
        <div class="move-field !mb-0">
          <label for="project-status">Status</label>
          <MoveSelect id="project-status" v-model="form.status" block :options="projectStatusOptions" />
        </div>
        <div class="move-field !mb-0">
          <label for="project-starts">Starts</label>
          <input id="project-starts" v-model="form.starts_on" type="date" class="move-input" />
        </div>
        <div class="move-field !mb-0">
          <label for="project-ends">Ends</label>
          <input id="project-ends" v-model="form.ends_on" type="date" class="move-input" />
        </div>
      </div>
      <div class="move-field !mb-0">
        <label for="project-description">Description</label>
        <textarea id="project-description" v-model="form.description" rows="3" class="move-textarea" placeholder="What this campaign covers…" />
      </div>
      <p v-if="formError" class="move-alert-error">{{ formError }}</p>
      <div class="prod-form-actions">
        <button type="submit" class="move-btn-primary" :disabled="saving">
          <span class="material-icons text-lg">check</span>
          {{ saving ? 'Saving…' : 'Create project' }}
        </button>
        <button type="button" class="move-btn-ghost" @click="showForm = false">Cancel</button>
      </div>
    </form>

    <div v-if="error" class="move-alert-error">{{ error }}</div>
    <div v-else-if="loading" class="flex justify-center py-16">
      <div class="move-spinner" aria-label="Loading projects" />
    </div>
    <div v-else-if="!projects.length" class="move-card move-empty-state py-16">
      <span class="material-icons">folder_special</span>
      <p class="font-semibold move-text text-lg">No projects yet</p>
      <p class="text-sm move-text-faint mt-1">Start with a campaign or series, then add productions under it.</p>
      <button v-if="!showForm" type="button" class="move-btn-primary mt-5" @click="showForm = true">
        <span class="material-icons text-lg">add</span>
        New project
      </button>
    </div>
    <div v-else class="space-y-3">
      <router-link
        v-for="project in projects"
        :key="project.id"
        :to="`/production/projects/${project.id}`"
        class="prod-list-card"
      >
        <div class="flex items-start justify-between gap-3">
          <div class="min-w-0">
            <p class="text-lg font-bold move-text">{{ project.name }}</p>
            <p v-if="project.description" class="mt-1 text-sm move-text-muted line-clamp-2">{{ project.description }}</p>
          </div>
          <span :class="statusBadgeClass(project.status)">{{ projectStatusLabels[project.status] || project.status }}</span>
        </div>
        <p class="mt-3 text-xs move-text-faint inline-flex flex-wrap items-center gap-x-3 gap-y-1">
          <span>{{ project.productions_count || 0 }} production{{ project.productions_count === 1 ? '' : 's' }}</span>
          <span v-if="project.starts_on">{{ formatDate(project.starts_on) }}</span>
        </p>
      </router-link>
    </div>
  </div>
</template>

<script setup>
import { onMounted, reactive, ref } from 'vue'
import ProductionPageHeader from '../../components/production/ProductionPageHeader.vue'
import ProductionSectionHead from '../../components/production/ProductionSectionHead.vue'
import { productionApi } from '../../services/productionApi'
import { projectStatusLabels, projectStatusOptions, statusBadgeClass } from '../../config/productionNavigation'
import { formatDate } from '../../utils/productionDates'

const loading = ref(true)
const error = ref('')
const formError = ref('')
const saving = ref(false)
const showForm = ref(false)
const projects = ref([])
const form = reactive({ name: '', description: '', status: 'active', starts_on: '', ends_on: '' })

async function load () {
  loading.value = true
  error.value = ''
  try {
    const body = await productionApi.listProjects()
    projects.value = body.data || []
  } catch (e) {
    error.value = e.response?.data?.message || 'Failed to load projects.'
  } finally {
    loading.value = false
  }
}

async function createProject () {
  saving.value = true
  formError.value = ''
  try {
    await productionApi.createProject({
      ...form,
      starts_on: form.starts_on || null,
      ends_on: form.ends_on || null
    })
    form.name = ''
    form.description = ''
    form.starts_on = ''
    form.ends_on = ''
    showForm.value = false
    await load()
  } catch (e) {
    formError.value = e.response?.data?.message || Object.values(e.response?.data?.errors || {}).flat().join(' ') || 'Could not create project.'
  } finally {
    saving.value = false
  }
}

onMounted(load)
</script>
